import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../config/apiService';
import { firstAllowedRoute } from './access';

// Distinct admin sign-in page at /SIP/admin -> #/admin/login.
// Separate from the supervisor login: posts to /sip/admin/login and stores
// admin_auth_token (not auth_token) so the two sessions don't collide.
function AdminLogin() {
    const [employeeID, setEmployeeID] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setBusy(true);
        try {
            const res = await adminAPI.login(parseInt(employeeID, 10), password);
            if (res.data?.token) {
                localStorage.setItem('admin_auth_token', res.data.token);
                localStorage.setItem('admin_employee_id', String(res.data.employeeID ?? ''));
                localStorage.setItem('admin_first_name', res.data.firstName || '');
                localStorage.setItem('admin_last_name', res.data.lastName || '');
                // Site half of {PROGRAM}-{SITE} still scopes the qual forms.
                localStorage.setItem('admin_access_visibility', res.data.accessVisibility || 'ALL-ALL');
                // Bitmask over SIPAdminAccess (1=Inventory ... 256=Rights
                // Management) — gates which pages this admin sees.
                const mask = Number(res.data.sipAdminAccessID ?? 0);
                localStorage.setItem('admin_access_mask', String(mask));
                const dest = firstAllowedRoute(mask);
                if (!dest) {
                    setError('Your account has no admin pages assigned. Contact the program administrator.');
                    return;
                }
                navigate(dest);
            } else {
                setError('Login failed.');
            }
        } catch (err) {
            const status = err.response?.status;
            setError(status === 401 ? 'Invalid Employee ID or password.' : 'Login failed. Please try again.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div style={styles.shell}>
            <div style={styles.card}>
                <div style={styles.eyebrow}>SIP Administration</div>
                <h1 style={styles.title}>Sign in</h1>
                <form onSubmit={handleSubmit}>
                    <label style={styles.label} htmlFor="adminEmployeeID">Employee ID</label>
                    <input
                        id="adminEmployeeID"
                        type="number"
                        autoComplete="username"
                        value={employeeID}
                        onChange={(e) => setEmployeeID(e.target.value)}
                        required
                        style={styles.input}
                    />
                    <label style={styles.label} htmlFor="adminPassword">Password</label>
                    <input
                        id="adminPassword"
                        type="password"
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        style={styles.input}
                    />
                    {error && <div style={styles.error}>{error}</div>}
                    <button type="submit" disabled={busy} style={{ ...styles.submit, opacity: busy ? 0.7 : 1, cursor: busy ? 'wait' : 'pointer' }}>
                        {busy ? 'Signing in…' : 'Sign in'}
                    </button>
                </form>
            </div>
        </div>
    );
}

const styles = {
    shell: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f3f4f6', padding: '20px' },
    card: { background: 'white', borderRadius: 12, padding: '32px 28px', maxWidth: 360, width: '100%', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' },
    eyebrow: { fontSize: 11, fontWeight: 700, color: '#9ca3af', letterSpacing: '0.1em', textTransform: 'uppercase', textAlign: 'center' },
    title: { fontSize: 22, fontWeight: 800, color: '#111827', margin: '6px 0 24px', textAlign: 'center' },
    label: { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 },
    input: { width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, marginBottom: 14 },
    error: { color: '#dc2626', fontSize: 12, marginBottom: 10 },
    submit: { width: '100%', padding: '10px', background: '#15803d', color: 'white', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 700 }
};

export default AdminLogin;
